import React, { useEffect, useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, FlaskConical, Beaker, Target, Trophy, X } from 'lucide-react';
import { getEnabledModes } from '../modes';
import { LabStation } from './LabStation';
import { MissionBriefing } from './MissionBriefing';
import { FamilyPicker, type FamilyPickerMode } from './FamilyPicker';
import { ChallengeAcceptModal } from './ChallengeAcceptModal';
import { FluencyIntro } from './FluencyIntro';
import { SprintIntro } from './SprintIntro';
import { FreePracticeIntro } from './FreePracticeIntro';
import { ChallengeIntro } from './ChallengeIntro';
import { FluencyModulePicker } from './FluencyModulePicker';
import { PageContainer } from '../components/shared/PageContainer';
import { itemVariants } from '../components/shared/animations';
import { WorldZone } from '../components/shared/WorldZone';
import { DjayAvatar, useDjaySelection, getDjayLabWelcome } from '../mascot';
import { useAppStore } from '../app/hooks/useAppStore';
import { getFamilyMasteryKey, getWeakestFamiliesGlobal } from '../mastery';
import { dataLayer, type PersonalRecords } from '../data/dataLayer';
import { isFluencyColdStart as checkFluencyColdStart } from '../modes';
import { humanizeFamilyId } from '../portals/teacher-v2/utils/familyDisplayNames';
// ─── Types ────────────────────────────────────────────────────
interface StartModeConfig {
  targetFamilyId?: string;
  targetModules?: string[];
}
interface LearningLabPageProps {
  onStartMode?: (modeId: string, config?: StartModeConfig) => void;
}
// ─── Component ────────────────────────────────────────────────
/**
 * LearningLabPage — Main page for the practice lab ("Oefenlab").
 *
 * Orchestrates mode selection, family picking, and challenge acceptance.
 * Shows Djay's mission briefing, training mode stations, and extra options.
 * Part of the learningLab domain (DDD co-locatie s57).
 */
export function LearningLabPage({
  onStartMode
}: LearningLabPageProps) {
  const allModes = getEnabledModes();
  const {
    selectedVariant
  } = useDjaySelection();
  const {
    state,
    selectedGroup
  } = useAppStore();
  const [lastSession, setLastSession] = useState<{
    modeId: string;
    familyId?: string;
    timestamp: number;
    score?: number;
  } | null>(null);
  const [newlyUnlockedFamilyId, setNewlyUnlockedFamilyId] = useState<string | null>(null);
  const [personalRecords, setPersonalRecords] = useState<PersonalRecords>({});
  useEffect(() => {
    dataLayer.getLastOefenlabSession().then(session => {
      if (session) {
        setLastSession(session);
      }
    });
    dataLayer.getNewlyUnlockedChallenge().then(familyId => {
      if (familyId) {
        setNewlyUnlockedFamilyId(familyId);
        dataLayer.clearNewlyUnlockedChallenge();
      }
    });
    dataLayer.getPersonalRecords().then(records => {
      setPersonalRecords(records);
    });
  }, []);
  const labWelcome = useMemo(() => getDjayLabWelcome(lastSession), [lastSession]);
  // ── FamilyPicker state ──
  const [familyPickerOpen, setFamilyPickerOpen] = useState(false);
  const [familyPickerMode, setFamilyPickerMode] = useState<FamilyPickerMode>('sprint');
  const [pendingModeId, setPendingModeId] = useState<string | null>(null);
  // ── ChallengeAcceptModal state ──
  const [challengeModalOpen, setChallengeModalOpen] = useState(false);
  const [pendingChallengeFamilyId, setPendingChallengeFamilyId] = useState<string | null>(null);
  // ── FluencyIntro state ──
  const [fluencyIntroOpen, setFluencyIntroOpen] = useState(false);
  const [pendingFluencyConfig, setPendingFluencyConfig] = useState<{
    familyId?: string;
  } | null>(null);
  // ── FluencyModulePicker state ──
  const [fluencyModulePickerOpen, setFluencyModulePickerOpen] = useState(false);
  // ── SprintIntro state ──
  const [sprintIntroOpen, setSprintIntroOpen] = useState(false);
  const [pendingSprintConfig, setPendingSprintConfig] = useState<{
    familyId?: string;
  } | null>(null);
  // ── FreePracticeIntro state ──
  const [freePracticeIntroOpen, setFreePracticeIntroOpen] = useState(false);
  const [pendingFreePracticeConfig, setPendingFreePracticeConfig] = useState<{
    familyId?: string;
  } | null>(null);
  // ── ChallengeIntro state ──
  const [challengeIntroOpen, setChallengeIntroOpen] = useState(false);
  // ── Personalization stats ──
  const groupSuffix = `_g${selectedGroup}`;
  const fluencyFamilies = useMemo(() => {
    return Object.entries(state.familyMastery || {}).filter(([key, s]) => key.endsWith(groupSuffix) && s.masteryScore >= 50 && s.masteryScore < 80);
  }, [state.familyMastery, groupSuffix]);
  const fluencyCount = fluencyFamilies.length;
  const fluencyMastery = useMemo(() => {
    if (fluencyCount === 0) return 0;
    return Math.round(fluencyFamilies.reduce((acc, [_, s]) => acc + s.masteryScore, 0) / fluencyCount);
  }, [fluencyFamilies, fluencyCount]);
  const challengeFamilies = useMemo(() => {
    return Object.entries(state.familyMastery || {}).filter(([key, s]) => key.endsWith(groupSuffix) && s.masteryScore >= 70);
  }, [state.familyMastery, groupSuffix]);
  const challengeCount = challengeFamilies.length;
  const challengeMastery = useMemo(() => {
    if (challengeCount === 0) return 0;
    return Math.round(challengeFamilies.reduce((acc, [_, s]) => acc + s.masteryScore, 0) / challengeCount);
  }, [challengeFamilies, challengeCount]);
  const weakestFamilyData = useMemo(() => {
    const weakest = getWeakestFamiliesGlobal(state.familyMastery || {}, selectedGroup, 1);
    return weakest.length > 0 ? weakest[0] : null;
  }, [state.familyMastery, selectedGroup]);
  const overallMastery = useMemo(() => {
    const families = Object.entries(state.familyMastery || {}).filter(([key, s]) => key.endsWith(groupSuffix) && s.attempts > 0);
    if (families.length === 0) return 0;
    const sum = families.reduce((acc, [_, s]) => acc + s.masteryScore, 0);
    return Math.round(sum / families.length);
  }, [state.familyMastery, groupSuffix]);
  const getEnrichmentForMode = (modeId: string) => {
    switch (modeId) {
      case 'fluency':
        return {
          statLabel: fluencyCount > 0 ? `${fluencyCount} onderwerpen klaar` : 'Train je snelheid',
          masteryPercent: fluencyCount > 0 ? fluencyMastery : undefined,
          personalRecord: personalRecords.fluency?.longestStreak ? `Record: ${personalRecords.fluency.longestStreak} op rij` : undefined
        };
      case 'sprint':
        return {
          statLabel: weakestFamilyData ? `${humanizeFamilyId(weakestFamilyData.familyId)}` : 'Oefen je zwakste onderwerpen',
          masteryPercent: weakestFamilyData ? weakestFamilyData.masteryScore : undefined,
          personalRecord: personalRecords.sprint?.bestScore ? `Beste score: ${personalRecords.sprint.bestScore}%` : undefined
        };
      case 'challenge':
        return {
          statLabel: challengeCount > 0 ? `${challengeCount} onderwerpen klaar` : 'Test je kennis',
          masteryPercent: challengeCount > 0 ? challengeMastery : undefined,
          personalRecord: personalRecords.challenge?.bestScore ? `Beste score: ${personalRecords.challenge.bestScore}%` : undefined
        };
      case 'free':
        return {
          statLabel: 'Alle vakken beschikbaar',
          masteryPercent: overallMastery > 0 ? overallMastery : undefined
        };
      default:
        return {};
    }
  };
  // ── Handlers ──
  const handleModeClick = (modeId: string, familyId?: string) => {
    if (modeId === 'fluency') {
      dataLayer.getFluencyIntroSeen().then(seen => {
        if (!seen) {
          setPendingFluencyConfig({
            familyId
          });
          setFluencyIntroOpen(true);
        } else {
          if (familyId && onStartMode) {
            onStartMode('fluency', {
              targetFamilyId: familyId
            });
          } else {
            setFluencyModulePickerOpen(true);
          }
        }
      });
      return;
    }
    if (modeId === 'sprint') {
      dataLayer.getSprintIntroSeen().then(seen => {
        if (!seen) {
          setPendingSprintConfig({
            familyId
          });
          setSprintIntroOpen(true);
        } else {
          proceedWithSprint(familyId);
        }
      });
      return;
    }
    if (modeId === 'free') {
      dataLayer.getFreePracticeIntroSeen().then(seen => {
        if (!seen) {
          setPendingFreePracticeConfig({
            familyId
          });
          setFreePracticeIntroOpen(true);
        } else {
          proceedWithFreePractice(familyId);
        }
      });
      return;
    }
    if (modeId === 'challenge') {
      dataLayer.getChallengeIntroSeen().then(seen => {
        if (!seen) {
          setChallengeIntroOpen(true);
        } else {
          proceedWithChallenge(familyId);
        }
      });
      return;
    }
    if (familyId && onStartMode) {
      onStartMode(modeId, {
        targetFamilyId: familyId
      });
      return;
    }
    onStartMode?.(modeId);
  };
  const proceedWithSprint = (familyId?: string) => {
    if (familyId && onStartMode) {
      onStartMode('sprint', {
        targetFamilyId: familyId
      });
      return;
    }
    setPendingModeId('sprint');
    setFamilyPickerMode('sprint');
    setFamilyPickerOpen(true);
  };
  const proceedWithFreePractice = (familyId?: string) => {
    if (familyId && onStartMode) {
      onStartMode('free', {
        targetFamilyId: familyId
      });
      return;
    }
    setPendingModeId('free');
    setFamilyPickerMode('free');
    setFamilyPickerOpen(true);
  };
  const proceedWithChallenge = (familyId?: string) => {
    if (familyId && onStartMode) {
      setPendingChallengeFamilyId(familyId);
      setChallengeModalOpen(true);
      return;
    }
    setPendingModeId('challenge');
    setFamilyPickerMode('challenge');
    setFamilyPickerOpen(true);
  };
  const handleChallengeIntroStart = () => {
    setChallengeIntroOpen(false);
    dataLayer.setChallengeIntroSeen(true);
    proceedWithChallenge();
  };
  const handleChallengeIntroCancel = () => {
    setChallengeIntroOpen(false);
  };
  const handleFamilySelect = (familyId: string) => {
    setFamilyPickerOpen(false);
    if (pendingModeId === 'challenge') {
      setPendingChallengeFamilyId(familyId);
      setChallengeModalOpen(true);
      setPendingModeId(null);
      return;
    }
    if (onStartMode && pendingModeId) {
      onStartMode(pendingModeId, {
        targetFamilyId: familyId
      });
    }
    setPendingModeId(null);
  };
  const handleFamilyPickerClose = () => {
    setFamilyPickerOpen(false);
    setPendingModeId(null);
  };
  const handleChallengeAccept = () => {
    setChallengeModalOpen(false);
    if (onStartMode && pendingChallengeFamilyId) {
      onStartMode('challenge', {
        targetFamilyId: pendingChallengeFamilyId
      });
    }
    setPendingChallengeFamilyId(null);
  };
  const handleChallengeDecline = () => {
    setChallengeModalOpen(false);
    setPendingChallengeFamilyId(null);
  };
  const handleFluencyStart = () => {
    setFluencyIntroOpen(false);
    dataLayer.setFluencyIntroSeen(true);
    if (pendingFluencyConfig?.familyId && onStartMode) {
      onStartMode('fluency', {
        targetFamilyId: pendingFluencyConfig.familyId
      });
    } else {
      setFluencyModulePickerOpen(true);
    }
    setPendingFluencyConfig(null);
  };
  const handleFluencyCancel = () => {
    setFluencyIntroOpen(false);
    setPendingFluencyConfig(null);
  };
  const handleSprintStart = () => {
    setSprintIntroOpen(false);
    dataLayer.setSprintIntroSeen(true);
    proceedWithSprint(pendingSprintConfig?.familyId);
    setPendingSprintConfig(null);
  };
  const handleSprintCancel = () => {
    setSprintIntroOpen(false);
    setPendingSprintConfig(null);
  };
  const handleFreePracticeStart = () => {
    setFreePracticeIntroOpen(false);
    dataLayer.setFreePracticeIntroSeen(true);
    proceedWithFreePractice(pendingFreePracticeConfig?.familyId);
    setPendingFreePracticeConfig(null);
  };
  const handleFreePracticeCancel = () => {
    setFreePracticeIntroOpen(false);
    setPendingFreePracticeConfig(null);
  };
  // ── Derived state ──
  const pendingChallengeMastery = pendingChallengeFamilyId ? state.familyMastery?.[getFamilyMasteryKey(pendingChallengeFamilyId, selectedGroup)]?.masteryScore ?? 0 : 0;
  // ── Fluency cold-start detection ──
  const isFluencyColdStart = useMemo(() => checkFluencyColdStart(state.familyMastery || {}, selectedGroup), [state.familyMastery, selectedGroup]);
  // ── FamilyPicker full-screen view ──
  if (familyPickerOpen) {
    return <FamilyPicker onClose={handleFamilyPickerClose} onSelect={handleFamilySelect} mode={familyPickerMode} data-id="element-2319" />;
  }
  // ── Main view ──
  return <PageContainer data-id="element-2320">
      {/* Newly Unlocked Challenge Notification */}
      <AnimatePresence data-id="element-2321">
        {newlyUnlockedFamilyId && <motion.div initial={{
        opacity: 0,
        y: -20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -20,
        scale: 0.95
      }} transition={{
        type: 'spring',
        bounce: 0.4,
        duration: 0.6
      }} className="mb-6 relative z-10" data-id="element-2322">
            <div className="bg-gradient-to-r from-amber-400 to-orange-500 p-1 rounded-2xl shadow-xl shadow-orange-500/20" data-id="element-2323">
              <div className="bg-white rounded-xl p-4 flex items-center gap-4 relative overflow-hidden" data-id="element-2324">
                <div className="absolute -right-4 -top-4 text-orange-100 opacity-50 transform rotate-12" data-id="element-2325">
                  <Trophy className="w-24 h-24" data-id="element-2326" />
                </div>

                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 relative z-10" data-id="element-2327">
                  <Trophy className="w-6 h-6 text-orange-500" data-id="element-2328" />
                </div>

                <div className="flex-1 relative z-10" data-id="element-2329">
                  <div className="text-xs font-bold text-orange-500 uppercase tracking-wider mb-0.5 flex items-center gap-1" data-id="element-2330">
                    <Sparkles className="w-3 h-3" data-id="element-2331" />
                    Uitdaging ontgrendeld!
                  </div>
                  <div className="text-slate-800 font-medium text-sm md:text-base" data-id="element-2332">
                    Je hebt 70% gehaald voor{' '}
                    <span className="font-bold" data-id="element-2333">
                      {humanizeFamilyId(newlyUnlockedFamilyId)}
                    </span>
                    .
                  </div>
                </div>

                <button onClick={() => setNewlyUnlockedFamilyId(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors relative z-10" aria-label="Sluiten" data-id="element-2334">
                  <X className="w-5 h-5" data-id="element-2335" />
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Compact Hero + Mission Area */}
      <motion.div variants={itemVariants} className="mb-8" data-id="element-2336">
        <div className="bg-violet-50/80 rounded-[2.5rem] p-6 sm:p-8 border-2 border-white shadow-sm" data-id="element-2337">
          {/* Header Row */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8" data-id="element-2338">
            <div className="flex items-center gap-4" data-id="element-2339">
              <div className="w-16 h-16 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0" data-id="element-2340">
                <DjayAvatar size="sm" emotion={labWelcome.emotion} variant={selectedVariant} animate={false} data-id="element-2341" />
              </div>
              <div data-id="element-2342">
                <h1 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tight" data-id="element-2343">
                  Oefenlab
                </h1>
                <p className="text-slate-600 font-medium text-sm sm:text-base" data-id="element-2344">
                  {labWelcome.text}
                </p>
              </div>
            </div>

            <div className="sm:ml-auto flex-shrink-0" data-id="element-2345">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-sm border border-slate-100" data-id="element-2346">
                <Sparkles className="w-4 h-4 text-violet-500" data-id="element-2347" />
                <span className="text-sm font-bold text-slate-700" data-id="element-2348">
                  {allModes.length} manieren om te oefenen
                </span>
              </div>
            </div>
          </div>

          {/* Mission Briefing */}
          <MissionBriefing onModeClick={handleModeClick} data-id="element-2349" />
        </div>
      </motion.div>

      {/* Lab Stations Grid */}
      <motion.div variants={itemVariants} data-id="element-2350">
        <WorldZone icon={Beaker} title="Hoe wil je oefenen?" accentColor="bg-fuchsia-100 text-fuchsia-600" djayMessage="Kies een manier om te oefenen! 🧪" djayVariant={selectedVariant} djayEmotion="excited" isLast={true} data-id="element-2351">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2352">
            {allModes.map(mode => {
            const enrichment = getEnrichmentForMode(mode.id);
            return <motion.div key={mode.id} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2 + allModes.indexOf(mode) * 0.05
            }} data-id="element-2353">
                  <LabStation mode={mode} statLabel={enrichment.statLabel} masteryPercent={enrichment.masteryPercent} personalRecord={enrichment.personalRecord} onClick={() => handleModeClick(mode.id)} data-id="element-2354" />
                </motion.div>;
          })}
          </div>
        </WorldZone>
      </motion.div>

      {/* ChallengeAcceptModal */}
      {pendingChallengeFamilyId && <ChallengeAcceptModal isOpen={challengeModalOpen} familyId={pendingChallengeFamilyId} masteryScore={pendingChallengeMastery} onAccept={handleChallengeAccept} onDecline={handleChallengeDecline} data-id="element-2355" />}

      {/* FluencyIntro Modal */}
      <FluencyIntro isOpen={fluencyIntroOpen} onStart={handleFluencyStart} onCancel={handleFluencyCancel} isColdStart={isFluencyColdStart} data-id="element-2356" />

      {/* FluencyModulePicker Modal */}
      <FluencyModulePicker isOpen={fluencyModulePickerOpen} onSelectAll={() => {
      setFluencyModulePickerOpen(false);
      onStartMode?.('fluency');
    }} onSelectModule={moduleId => {
      setFluencyModulePickerOpen(false);
      onStartMode?.('fluency', {
        targetModules: [moduleId]
      });
    }} onCancel={() => setFluencyModulePickerOpen(false)} data-id="element-2357" />

      {/* SprintIntro Modal */}
      <SprintIntro isOpen={sprintIntroOpen} onStart={handleSprintStart} onCancel={handleSprintCancel} data-id="element-2358" />

      {/* FreePracticeIntro Modal */}
      <FreePracticeIntro isOpen={freePracticeIntroOpen} onStart={handleFreePracticeStart} onCancel={handleFreePracticeCancel} data-id="element-2359" />

      {/* ChallengeIntro Modal */}
      <ChallengeIntro isOpen={challengeIntroOpen} onStart={handleChallengeIntroStart} onCancel={handleChallengeIntroCancel} data-id="element-2360" />
    </PageContainer>;
}